import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EscalationStatusResp } from '../../services/hitl';
import { useHitlApi } from './HitlApiContext';
import { getSocket } from '../../services/socket';

const HITL_AMBER = '#B8600B';
const HITL_AMBER_LIGHT = '#F5E6D8';

interface Props {
  escalationId: string;
  onStatus: (status: EscalationStatusResp) => void;
  onCancelled: () => void;
}

function fmtWait(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds - minutes * 60;
  return remainder === 0 ? `${minutes} min` : `${minutes} min ${remainder}s`;
}

const HitlWaitingBanner: React.FC<Props> = ({ escalationId, onStatus, onCancelled }) => {
  const { t } = useTranslation();
  const hitlApi = useHitlApi();
  const [status, setStatus] = useState<EscalationStatusResp | null>(null);
  const [now, setNow] = useState(Date.now());
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const next = await hitlApi.getStatus(escalationId);
    setStatus(next);
    setError(null);
    if (next.status !== 'waiting') onStatus(next);
  }, [escalationId, hitlApi, onStatus]);

  useEffect(() => {
    let active = true;
    const poll = () => {
      void refresh().catch(() => {
        if (active) setError('Status could not be refreshed. We will keep trying.');
      });
    };
    poll();
    const pollId = window.setInterval(poll, 5_000);
    const tickId = window.setInterval(() => setNow(Date.now()), 1_000);
    const socket = getSocket();
    const onAssigned = (payload: { escalation_id?: string }) => {
      if (payload.escalation_id === escalationId) poll();
    };
    socket.on('hitl:session:assigned', onAssigned);
    return () => {
      active = false;
      window.clearInterval(pollId);
      window.clearInterval(tickId);
      socket.off('hitl:session:assigned', onAssigned);
    };
  }, [escalationId, refresh]);

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);
    try {
      await hitlApi.cancel(escalationId);
      onCancelled();
    } catch (err) {
      // A 409 commonly means an instructor accepted during the click. Refresh
      // first so the UI transitions to the authoritative live state.
      try {
        await refresh();
      } catch {
        setError(err instanceof Error ? err.message : 'The request could not be cancelled');
      }
    } finally {
      setCancelling(false);
    }
  };

  const requestedAt = status ? Date.parse(status.requested_at) : now;
  const waited = Math.max(0, now - requestedAt);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        padding: 16,
        background: HITL_AMBER_LIGHT,
        border: `1px solid ${HITL_AMBER}`,
        borderRadius: 8,
        color: '#18181B',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          aria-hidden
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: `3px solid ${HITL_AMBER}`,
            borderTopColor: 'transparent',
            animation: 'hitl-spin 1s linear infinite',
            flex: '0 0 auto',
          }}
        />
        <style>{`@keyframes hitl-spin { to { transform: rotate(360deg); } } @media (prefers-reduced-motion: reduce) { [role="status"] div { animation-duration: 2s; } }`}</style>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Waiting for an instructor</div>
          <div style={{ fontSize: 13, color: '#52525B', marginTop: 2 }}>
            {t('ako.hitl_waiting', { wait: fmtWait(waited), name: t('companion.name') })}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void handleCancel()}
          disabled={cancelling}
          style={{
            minHeight: 40,
            padding: '8px 12px',
            border: `1px solid ${HITL_AMBER}`,
            background: '#fff',
            color: HITL_AMBER,
            borderRadius: 6,
            fontFamily: 'inherit',
            fontSize: 13,
            cursor: cancelling ? 'wait' : 'pointer',
          }}
        >
          {cancelling ? 'Cancelling…' : 'Cancel request'}
        </button>
      </div>
      {error && <div role="alert" style={{ marginTop: 8, color: '#9F1239', fontSize: 12 }}>{error}</div>}
    </div>
  );
};

export { fmtWait };
export default HitlWaitingBanner;
